// Modèle éditable de la fiche Gym'Coach côté site (spec 2026-09-26, § 8) et
// ses conversions : depuis la réponse de l'API, vers le corps d'enregistrement.
// Tout ce qui est enregistré depuis le site est signé coach (D8).
//
// Modèle :
//   { version, verrouillee, personnaliseeParCoach, resume: string,
//     exercices: [{ idExercice, nom, texte: string,
//                   series: [{ ordre, poids: string, reps: string, rpe: string, texte: string,
//                              retiree: boolean, repsPrecedentes, poidsPrecedent }] }] }
//
// Les champs sont des chaînes : ce sont des valeurs d'input. La conversion en
// nombres n'a lieu qu'à l'enregistrement. Pur : pas de React, pas de fetch.

const enTexte = (v) => (v == null ? "" : String(v));

export const nombreOuNull = (v) => {
	if (v == null || String(v).trim() === "") return null;
	const n = Number(String(v).trim().replace(",", "."));
	return Number.isFinite(n) ? n : null;
};

export const entierOuNull = (v) => {
	const n = nombreOuNull(v);
	return n == null ? null : Math.round(n);
};

export const texteOuNull = (v) => {
	const t = enTexte(v).trim();
	return t.length ? t : null;
};

// Le RPE, même règle que l'app (rpeOuNull) : un nombre de 1 à 10, virgule
// acceptée, renvoyé en chaîne normalisée ("8,5" -> "8.5"). Tout le reste
// (fourchette, texte, hors bornes) donne null.
export const rpeOuNull = (v) => {
	if (v == null || String(v).trim() === "") return null;
	const n = Number(String(v).trim().replace(",", "."));
	return Number.isFinite(n) && n >= 1 && n <= 10 ? String(n) : null;
};

// Les exercices que l'éditeur affiche : ceux qu'il n'a pas marqués supprimés
// et qui existent déjà côté serveur (un exercice pas encore enregistré porte
// id -1 : l'API ne peut pas lui rattacher de consigne).
const exercicesActifs = (training) =>
	((training && training.exercices) || []).filter((ex) => ex && ex.supprime !== 1 && ex.id > 0);

const serieVide = (ordre) => ({
	ordre, poids: "", reps: "", rpe: "", texte: "", retiree: false, repsPrecedentes: null, poidsPrecedent: null,
});

// Le panneau vide : une ligne par série d'après les exercices de la séance.
export const modeleVide = (training) => ({
	version: 0,
	verrouillee: false,
	personnaliseeParCoach: false,
	resume: "",
	exercices: exercicesActifs(training).map((ex) => ({
		idExercice: ex.id,
		nom: ex.nom || "",
		texte: "",
		series: (ex.series || []).map((_, i) => serieVide(i)),
	})),
});

// La fiche de l'API, alignée sur les exercices et séries de la séance ouverte :
// un exercice ou une série sans ligne reste vide ; une ligne de l'API sans
// série correspondante (séance modifiée depuis) est ignorée.
export const modeleDepuisFiche = (fiche, training) => {
	const base = modeleVide(training);
	const parExo = new Map(((fiche && fiche.exercices) || []).map((e) => [e.idExercice, e]));
	return {
		...base,
		version: typeof fiche?.version === "number" ? fiche.version : 0,
		verrouillee: fiche?.verrouillee === true,
		personnaliseeParCoach: fiche?.personnaliseeParCoach === true,
		resume: enTexte(fiche?.resume?.texte),
		exercices: base.exercices.map((exo) => {
			const ligneExo = parExo.get(exo.idExercice);
			if (!ligneExo) return exo;
			const parOrdre = new Map((ligneExo.series || []).map((s) => [s.ordre, s]));
			return {
				...exo,
				texte: enTexte(ligneExo.texte && ligneExo.texte.valeur),
				series: exo.series.map((s) => {
					const l = parOrdre.get(s.ordre);
					if (!l) return s;
					return {
						ordre: s.ordre,
						poids: enTexte(l.poids),
						reps: enTexte(l.reps),
						rpe: enTexte(l.rpe),
						texte: enTexte(l.texte),
						retiree: l.supprimee === true,
						repsPrecedentes: l.repsPrecedentes ?? null,
						poidsPrecedent: l.poidsPrecedent ?? null,
					};
				}),
			};
		}),
	};
};

// « Préremplir avec Gym'Coach » : les valeurs du brouillon remplacent les champs ;
// version, verrou et signature de la fiche réelle sont conservés (le
// brouillon est en version 0). Le modèle sert de séance de référence : mêmes
// exercices, mêmes séries.
export const appliquerBrouillon = (modele, brouillon) => {
	const reference = {
		exercices: modele.exercices.map((e) => ({ id: e.idExercice, nom: e.nom, series: e.series })),
	};
	const rempli = modeleDepuisFiche(brouillon, reference);
	return {
		...rempli,
		version: modele.version,
		verrouillee: modele.verrouillee,
		personnaliseeParCoach: modele.personnaliseeParCoach,
	};
};

const serieSansConsigne = (s) =>
	!s.retiree &&
	nombreOuNull(s.poids) == null &&
	entierOuNull(s.reps) == null &&
	rpeOuNull(s.rpe) == null &&
	texteOuNull(s.texte) == null;

// Les séries qui partiraient vides à l'enregistrement, donc retirées (D8).
export const seriesSansConsigne = (modele) =>
	modele.exercices.reduce((n, e) => n + e.series.filter(serieSansConsigne).length, 0);

export const modeleADesValeurs = (modele) =>
	texteOuNull(modele.resume) != null ||
	modele.exercices.some(
		(e) => texteOuNull(e.texte) != null || e.series.some((s) => s.retiree || !serieSansConsigne(s))
	);

// Les champs chiffrés saisis mais illisibles ("82,5 kg", "8-10", "-5", RPE
// hors de 1 à 10) : ils partiraient à null sans que le coach le sache, donc
// l'enregistrement les refuse. Un champ vide n'est jamais invalide, une série
// retirée non plus (elle part vide).
export const champsInvalides = (modele) => {
	const invalides = [];
	((modele && modele.exercices) || []).forEach((e) => {
		(e.series || []).forEach((s) => {
			if (s.retiree) return;
			const signaler = (champ) => invalides.push({ idExercice: e.idExercice, ordre: s.ordre, champ });
			const poids = nombreOuNull(s.poids);
			if (texteOuNull(s.poids) != null && (poids == null || poids < 0)) signaler("poids");
			const reps = entierOuNull(s.reps);
			if (texteOuNull(s.reps) != null && (reps == null || reps < 0)) signaler("reps");
			if (texteOuNull(s.rpe) != null && rpeOuNull(s.rpe) == null) signaler("rpe");
		});
	});
	return invalides;
};

// Le corps de POST /coach/preview/editer, tout signé coach (D8). Une série
// retirée part vide : côté API, une ligne coach vide est une suppression.
// `version` : celle de la fiche affichée (0 = pas encore de fiche). L'API
// refuse l'édition (« conflit ») si la fiche a changé depuis.
export const corpsToutCoach = (modele) => ({
	version: modele.version,
	resume: { texte: texteOuNull(modele.resume), source: "coach" },
	exercices: modele.exercices.map((e) => ({
		idExercice: e.idExercice,
		texte: texteOuNull(e.texte),
		source: "coach",
		series: e.series.map((s) =>
			s.retiree
				? { ordre: s.ordre, poids: null, reps: null, rpe: null, texte: null, source: "coach" }
				: {
						ordre: s.ordre,
						poids: nombreOuNull(s.poids),
						reps: entierOuNull(s.reps),
						rpe: rpeOuNull(s.rpe),
						texte: texteOuNull(s.texte),
						source: "coach",
				  }
		),
	})),
});
